import { Core } from "@faire/design-tokens";
import { BrandShippingLocationText } from "@faire/retailer-visitor-shared/views/brand/BrandHeader/BrandShippingLocationText";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { BrandRating } from "@faire/retailer-visitor-shared/views/suppliers/__internal__/LazySuppliersPage/__internal__/SuppliersPage/__internal__/LoggedOutSuppliersPage/__internal__/Components/LoggedOutSuppliersPageBrandReviewTile/__internal__/BrandRating";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { formatMoney } from "@faire/web--source/common/money";
import { Avatar } from "@faire/web--source/slate/Avatar";
import { Row } from "@faire/web--source/slate/Layout";
import { Column } from "@faire/web--source/slate/Layout/Column";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { NoTranslate } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackSeoSuppliersPageBrandListBrandTileClick } from "@faire/web-api--source/events/seo/click/suppliersPageBrandListBrandTile";
import { ISearchBrandsV2Response } from "@faire/web-api--source/indigofair/data/ISearchBrandsV2Response";
import brandUrl from "@faire/web-api--source/routes/www/brand/brandToken";
import React from "react";
import styled from "styled-components";

interface IProps {
  brand: ISearchBrandsV2Response.IBrand | undefined;
  categoryPathIdentifier: string;
}

export const BrandInfo: React.FC<IProps> = ({
  brand,
  categoryPathIdentifier,
}) => {
  const { strictLocalize } = useStrictLocalization();
  if (!brand?.token) {
    return null;
  }
  const {
    token,
    image,
    name,
    brand_reviews_summary,
    ships_from_country,
    minimum_order_amount,
  } = brand;

  const minimum = minimum_order_amount
    ? strictLocalize(
        {
          defaultMessage: "{minMoney} min",
          description: {
            text: "minMoney is the minimum a retailer must order to be able to order from this brand",
            img: "https://cdn.faire.com/fastly/49c2b53d16ec4a32fc5a7de143dc0d994492dfd02dfed6be70d31d4955809ed3.png",
          },
        },
        {
          minMoney: formatMoney({
            money: minimum_order_amount,
            omitCentsIfZero: true,
          }),
        }
      )
    : null;
  return (
    <Wrapper
      to={brandUrl(token)}
      onClick={() =>
        trackSeoSuppliersPageBrandListBrandTileClick(
          token,
          categoryPathIdentifier
        )
      }
    >
      <Avatar
        className="max-w-max"
        size={{
          mobileAndAbove: "smaller",
          tabletAndAbove: "large",
        }}
        image={image}
        altTag={name}
      />
      <Spacer width="2x" />
      <ContentWrapper>
        <Typography
          maxLines={2}
          variant={{
            mobileAndAbove: "paragraphSansMedium",
            tabletAndAbove: "subheadingSansMedium",
          }}
        >
          <NoTranslate>{name}</NoTranslate>
        </Typography>
        <Spacer width={4} />
        <Row>
          {brand_reviews_summary ? (
            <BrandRating brandReviewSummary={brand_reviews_summary} />
          ) : null}
          {brand_reviews_summary && minimum ? (
            <>
              <Spacer width={4} />
              {" · "}
              <Spacer width={4} />
            </>
          ) : null}
          {minimum ? <Typography>{minimum}</Typography> : null}
        </Row>

        <Typography color={Core.text.subdued} variant="paragraphSansRegular">
          <BrandShippingLocationText shipsFromCountry={ships_from_country} />
        </Typography>
      </ContentWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Link)`
  display: flex;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  max-width: 380px;
  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 0;
    align-items: center;
  }
`;
const ContentWrapper = styled(Column).attrs({
  justify: "center",
  align: "flex-start",
})`
  width: 100%;
  align-self: stretch;
  overflow: hidden;
`;
